import {DatePipe} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  MAT_PAGINATOR_DEFAULT_OPTIONS,
  MatPaginatorDefaultOptions
} from '@angular/material/paginator';
import {Router} from '@angular/router';
import {Actions} from '@ngneat/effects-ng';
import {TranslateService} from '@ngx-translate/core';
import {
  AttachmentObject,
  constants,
  IntercomEventsByArea,
  IntercomEventService,
  NodePath,
  REF_MODEL_STORE,
  RefModelStoreService,
  SnackBarService,
  TakeUntilComponentDestroy
} from '@q9elements/ui-kit/common';
import {
  filterEntities,
  GridQuery,
  GridStore,
  loadEntities,
  provideCustomGridFilters,
  Q9GridComponent,
  removeEntity,
  SORT_SETTINGS,
  sortEntities
} from '@q9elements/ui-kit/components';

import {RefModelSidebarService} from '@q9elements/ui-kit/refmodels';
import {map as _map, cloneDeep, get, isEqual, pick, set} from 'lodash';
import {BehaviorSubject, Subscription} from 'rxjs';
import {filter, pairwise, tap} from 'rxjs/operators';
import {IGridColumn} from 'src/app/custom-grid/models/grid-column.interface';
import {
  ICustomView,
  ICustomViewColumn,
  ICustomViewFilter
} from 'src/app/editor-interface/models/custom-view.interface';
import {CustomViewColumnStylesService} from 'src/app/editor-interface/services/custom-view-column-styles.service';
import {getObjectWithMaxTags} from 'src/app/util/grid-context-menu.utils';
import {CustomViewDaoService} from '../../../core/services/custom-view-dao.service';
import {GridSelectorService} from '../../../custom-grid/services/grid-selector.service';
import {TreeUpdateObject} from '../../../models/rme/tree-update-object';
import {ContextMenuActionsService} from '../../../new-context-menu/services/context-menu-actions.service';
import {ContextMenuViewService} from '../../../new-context-menu/services/context-menu-view.service';
import {UpdateTreeService} from '../../../services/update-tree.service';
import {RUBICS_CUBE_COLUMNS} from '../../models/column-styles.interface';
import {ColumnTypeFilterMapping} from '../../models/consts/ColumnTypeFilterMapping';
import {CustomViewService} from '../../services/custom-view.service';
import {GridUpdateService} from '../../services/grid-update.service';
import {NodeRemoveEmitterService} from '../../services/node-remove-emitter.service';
import CustomMetadataQuery from './state/custom-metadata.query';
import {CustomMetadataStore} from './state/custom-metadata.store';

const ID_FIELD = 'id';
const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 100;
const AVERAGE_TAG_WIDTH = 90;
const DEFAULT_COLUMN_WIDTH = 300;
const DAY_FORMAT = 'MMMM dd, yyyy';
const CHECKBOX_COLUMN_WIDTH = 64;
const GAP_WIDTH = 16;
const FIELDS_NOT_UPDATE = ['name'];
const TAGS_PATH = 'sfNode.tags';
const OPTIMIZE_STATUS_PATH = 'sfNode.optimize.optimizeStatus.value';
const OPTIMIZE_ASSIGNEE_PATH = 'sfNode.optimize.optimizeAssignee.email';
const CNA_PATH = 'sfNode.customNodeAttributes';
const OPTIMIZE_PROPERTIES = ['optimizeStatus', 'optimizeAssignee'];
const SF_NODE_PROPERTIES = ['customNodeAttributes'];
const BULK_UPDATABLE_COLUMNS_PATH = {
  optimization_status: OPTIMIZE_STATUS_PATH,
  node_assignee: OPTIMIZE_ASSIGNEE_PATH,
  tags: TAGS_PATH
};

@Component({
  selector: 'app-custom-metadata-view',
  templateUrl: './custom-metadata-view.component.html',
  styleUrls: ['./custom-metadata-view.component.scss'],
  providers: [
    {provide: GridStore, useClass: CustomMetadataStore},
    {provide: GridQuery, useClass: CustomMetadataQuery},
    {
      provide: MAT_PAGINATOR_DEFAULT_OPTIONS,
      useValue: {
        pageSize: DEFAULT_PAGE_SIZE,
        pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS
      } as MatPaginatorDefaultOptions
    },
    provideCustomGridFilters(ColumnTypeFilterMapping)
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomMetadataViewComponent
  extends TakeUntilComponentDestroy
  implements OnInit, OnDestroy
{
  @ViewChild('grid') grid: Q9GridComponent;

  @Input() hideNodePath = false;
  @Input() set filterable(value: boolean) {
    this.filterableState.set(value);
  }
  /** When set, overrides default editor/external multi-select rules (e.g. Change Monitoring alerts). */
  @Input() enableMultiSelectOverride?: boolean;
  @Input() disableContextMenu = false;

  /** Optional cell templates keyed by grid column `field` — bypasses HTML encode pipe (see q9-grid `templates`). */
  @Input() supplementalGridTemplates: Record<string, TemplateRef<{entity: unknown}>> = {};

  @Input()
  public set customMetadataView(value: ICustomView) {
    this.isSystemView.set(Boolean(value?.system));
    this._customMetadataView$.next(value);
  }

  @Input()
  public set dynamicalViewConfig(config: any) {
    if (!config) {
      return;
    }

    const {fetchConfig, view} = config;

    this.gridQuery['setFetchConfig'](fetchConfig);
    this._customMetadataView$.next(view);
  }

  @Output() public selectRow = new EventEmitter<ICustomView & {id: string}>();

  private _customMetadataView$ = new BehaviorSubject<ICustomView | null>(null);
  private customView$ = this._customMetadataView$.pipe(
    filter((value): value is ICustomView => value !== null)
  );
  private gdprStatuses: any;
  private updateTagsColumnWidth$ = new BehaviorSubject<number>(DEFAULT_COLUMN_WIDTH);
  private selectedNode: ICustomView;
  private lastViewStructureSignature: string | null = null;
  private rowSelectedSubscription: Subscription;
  private customViewWidth: number;
  private injector: Injector = inject(Injector);
  private readonly userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  public columns$ = new BehaviorSubject<IGridColumn<ICustomView>[]>([]);
  public nodePath$ = this.rmeStore.select<NodePath[]>(REF_MODEL_STORE.NODE_PATH);
  public menuItems: any;
  public readonly filterableState = signal(true);
  public readonly isSystemView = signal(false);
  /** Viewers cannot filter/sort shared (global/system) views. */
  public readonly disableSharedViewControls = signal(false);

  public readonly gridSortable = computed(() =>
    this.disableSharedViewControls() ? false : SORT_SETTINGS
  );

  /** Effective grid filtering: the host's `filterable` input, system views and
   *  viewer-locked shared views all make the grid functionally non-filterable. */
  public readonly gridFilterable = computed(() =>
    this.filterableState() && !this.isSystemView() && !this.disableSharedViewControls()
      ? ('menu' as const)
      : false
  );

  public get isMultipleMode(): boolean {
    if (this.enableMultiSelectOverride !== undefined) {
      return this.enableMultiSelectOverride;
    }
    return this.rmeStore.isEditor && this.rmeStore.refModel.external;
  }

  protected actions: Actions = inject(Actions);
  readonly idField = '_id';

  constructor(
    private columnStylesService: CustomViewColumnStylesService,
    private date: DatePipe,
    private rmeStore: RefModelStoreService,
    private contextMenuViewService: ContextMenuViewService,
    private contextMenuActionsService: ContextMenuActionsService,
    private nodeRemoveEmitterService: NodeRemoveEmitterService,
    private updateTreeService: UpdateTreeService,
    private gridUpdateService: GridUpdateService,
    public selector: GridSelectorService,
    private gridQuery: GridQuery,
    private gridStore: GridStore,
    private router: Router,
    private refModelSidebarService: RefModelSidebarService,
    private intercomEventService: IntercomEventService,
    private customViewDaoService: CustomViewDaoService,
    private customViewService: CustomViewService,
    private translate: TranslateService,
    private snackBarService: SnackBarService
  ) {
    super();
    this.setDefaultWidth();
  }

  ngOnInit(): void {
    this.initSelectedNodeSubscription();
    this.initGDPRStatuses();
    this.initNodeRemoveEmitterSubscription();
    this.initUpdateNodeInTreeSubscription();
    this.initSelectedNodeAttachmentsStoreSubscription();
    this.initNodesBulkUpdateSubscription();
    this.updateInitialWidth();
    this.initializeColumns();
    this.initColumnsWidthUpdate();
    this.onRowSelected();
    this.setUpEffect();
    this.fireIntercomEvent();
  }

  setUpEffect() {
    effect(
      () => {
        if (this.refModelSidebarService.reloadNodeTreeData()) {
          this.refModelSidebarService.reloadNodeTreeData.set(false);
          this.gridStore.update(state => ({
            ...state,
            reselectActiveEntity: true
          }));
          this.actions.dispatch(loadEntities({reset: true}));
        }
      },
      {allowSignalWrites: true, injector: this.injector}
    );
  }

  private fireIntercomEvent() {
    const currentUrl = this.router.url;
    const refModel = this.refModelSidebarService.refModelMeta();

    if (currentUrl.endsWith('/standard')) {
      this.intercomEventService.sendIntercomEvent(
        refModel,
        IntercomEventsByArea.SALESFORCE_METADATA.STANDARD_METADATA_VIEW_OPENED
      );
    } else if (currentUrl.includes('/customView')) {
      this.intercomEventService.sendIntercomEvent(
        refModel,
        IntercomEventsByArea.SALESFORCE_METADATA.CUSTOM_METADATA_VIEW_OPENED
      );
    }
  }

  private setDefaultWidth(): void {
    this.customViewWidth = window.innerWidth - GAP_WIDTH * 2 - CHECKBOX_COLUMN_WIDTH;
  }

  private initializeColumns(): void {
    this.customView$
      .pipe(
        filter(customView => customView !== null),
        this.takeUntilComponentDestroy(),
        tap(customView => {
          this.disableSharedViewControls.set(
            !this.rmeStore.isEditor && !customView.private && !customView.dynamicalView
          );

          const currentView = cloneDeep(customView);
          const {filters, sort: sortFromDb} = currentView;
          filters.map(filter => {
            filter['field'] = filter.column;
            delete filter.column;

            return filter;
          });
          this.gridStore.update({filters});
          if (sortFromDb) {
            const sort = [
              {
                dir: sortFromDb.direction as 'asc' | 'desc',
                field: sortFromDb.column
              }
            ];
            this.gridStore.update({sort});
          }

          // Only (re)generate columns when the view's structure actually changed. This is the
          // expensive part we want to skip on filter/sort-only emissions to avoid resetting the
          // grid's column/filter-menu state.
          const structureSignature = this.viewStructureSignature(currentView);
          if (structureSignature !== this.lastViewStructureSignature) {
            this.lastViewStructureSignature = structureSignature;
            const generatedColumns = this.generateColumns(currentView.columns);

            this.columns$.next(generatedColumns);
            this.gridQuery.store.update(state => ({
              ...state,
              refModel: customView.refModel,
              viewId: customView._id,
              queryParams: {
                skip: this.gridQuery.getValue().skip,
                limit: this.gridQuery.getValue().take
              },
              columns: generatedColumns
            }));
          }
        })
      )
      .subscribe(() => {
        this.actions.dispatch(loadEntities({reset: true}));
      });
  }

  /**
   * Identifies a view by structure (id + column identity). `initializeColumns` uses it to skip
   * regenerating columns on emissions that only changed filters/sort, while still re-syncing
   * filters/sort and reloading. Comparing against the last seen signature means switching between
   * different views (A -> B -> A) still re-generates columns.
   */
  private viewStructureSignature(view: ICustomView): string {
    const columns = (view.columns ?? [])
      .map(column =>
        column.hasSubcolumns ? `${column.value}:${column.subcolumn?.value}` : column.value
      )
      .join(',');

    return `${view._id}|${columns}`;
  }

  private initColumnsWidthUpdate(): void {
    this.updateTagsColumnWidth$.pipe(this.takeUntilComponentDestroy()).subscribe(tagsWidth => {
      const generatedColumns = this.columns$.getValue();
      let columnsCount = generatedColumns.length;
      const isSmallColumnSet = columnsCount <= 5;

      const updatedColumns = generatedColumns.map(column => {
        if (column.field === RUBICS_CUBE_COLUMNS.TAGS) {
          if (isSmallColumnSet) {
            this.setDefaultWidth();
            this.customViewWidth -= tagsWidth;
            columnsCount--;
          }

          return {...column, width: tagsWidth};
        }

        if (isSmallColumnSet) {
          return {...column, width: this.customViewWidth / columnsCount};
        }

        return column;
      });

      this.columns$.next(updatedColumns);

      this.gridQuery.store.update('columns', () => updatedColumns);
    });
  }

  private generateColumns(columns: ICustomViewColumn[]): IGridColumn<ICustomView>[] {
    return columns.filter(column => !column.filterOnly).map(col => this.createColumnConfig(col));
  }

  private createColumnConfig(col: ICustomViewColumn): IGridColumn<ICustomView> {
    const {
      hasSubcolumns,
      value,
      displayValue,
      subcolumn,
      valueType,
      isCustomAttribute,
      filter,
      disableSort,
      options
    } = col;

    return {
      width: col.width ?? (valueType === 'integer' ? 100 : 300),
      field: hasSubcolumns ? subcolumn?.value : value,
      title: hasSubcolumns ? subcolumn?.displayValue : displayValue,
      isCustomAttribute,
      ...(value === RUBICS_CUBE_COLUMNS.TAGS && {minResizableWidth: 300}),
      getColumnContent: dataItem => this.getColumnContent(col, dataItem),
      customClass: dataItem => this.getColumnCustomClass(col, dataItem),
      filter,
      disableSort,
      options
    };
  }

  private getColumnContent(col: ICustomViewColumn, dataItem: any): any {
    if (col.formatDisplay) {
      const fieldName = col.hasSubcolumns ? col.subcolumn?.value : col.value;
      return {[fieldName]: col.formatDisplay(dataItem)};
    }

    const {hasSubcolumns, subcolumn, value} = col;
    const columnValue = this.getColumnValue(col, dataItem);
    const columnName = hasSubcolumns ? subcolumn?.value : value;
    const formatted = {[columnName]: ''};

    switch (col.valueType) {
      case 'date':
        formatted[columnName] = this.formatDate(columnValue, col.dateOnly);
        break;
      case 'integer':
        formatted[columnName] = columnValue.toString();
        break;
      default:
        formatted[columnName] = columnValue;
    }

    return formatted;
  }

  private getColumnCustomClass(col: ICustomViewColumn, dataItem: any): string {
    const colValue = this.getColumnValue(col, dataItem);
    const {classes} = this.columnStylesService.getStyles(col.value, colValue) || {};
    return Array.isArray(classes) ? classes.filter(Boolean).join(' ') : classes || '';
  }

  private initGDPRStatuses(): void {
    this.gdprStatuses = {};
    _map(constants.gdprOptions.gdprStatuses, item => {
      this.gdprStatuses[item.key] = item.value;
    });
  }

  private initNodeRemoveEmitterSubscription() {
    this.nodeRemoveEmitterService
      .subscribeToEmitter()
      .pipe(this.takeUntilComponentDestroy())
      .subscribe(nodeDate => {
        this.actions.dispatch(removeEntity({id: nodeDate.nodeId}));
      });
  }

  private initSelectedNodeAttachmentsStoreSubscription(): void {
    this.rmeStore
      .select(REF_MODEL_STORE.SELECTED_NODE_ATTACHMENTS)
      .pipe(this.takeUntilComponentDestroy())
      .subscribe((attachmentsObj: AttachmentObject) =>
        this.gridUpdateService.updateAttachments(attachmentsObj, ID_FIELD)
      );
  }

  private initUpdateNodeInTreeSubscription(): void {
    this.updateTreeService
      .select('updateNodeInTree')
      .pipe(
        this.takeUntilComponentDestroy(),
        filter(data => data && !FIELDS_NOT_UPDATE.includes(data.property))
      )
      .subscribe((data: TreeUpdateObject) => {
        if (data.property === 'tags') {
          this.updateTagsWidth(data.value);
        }

        if (OPTIMIZE_PROPERTIES.includes(data.property)) {
          switch (data.property) {
            case 'optimizeStatus':
              data.property = OPTIMIZE_STATUS_PATH;
              break;
            case 'optimizeAssignee':
              data.property = OPTIMIZE_ASSIGNEE_PATH;
              break;
          }
        }

        if (SF_NODE_PROPERTIES.includes(data.property)) {
          data.property = 'sfNode.' + data.property;
        }

        const updatedColumn = this.getUpdatedColumns(data);

        this.gridQuery.store.update(data.nodeId, (entity: any) => {
          return {
            ...entity,
            ...updatedColumn
          };
        });
      });
  }

  private updateInitialWidth(): void {
    this.gridQuery
      .selectAll()
      .pipe(
        this.takeUntilComponentDestroy(),
        pairwise(),
        filter(([prev, curr]) => !isEqual(prev, curr))
      )
      .subscribe(([_, updatedNodes]) => {
        if (updatedNodes) {
          const objWithMaxTags = getObjectWithMaxTags(updatedNodes);
          if (objWithMaxTags) {
            this.updateTagsWidth(objWithMaxTags['tags']);
          }
        }
      });
  }

  private initSelectedNodeSubscription(): void {
    this.rmeStore
      .select(REF_MODEL_STORE.SELECTED_NODE)
      .pipe(this.takeUntilComponentDestroy())
      .subscribe((selectedNode): void => {
        if (selectedNode) {
          this.selectedNode = selectedNode as ICustomView;
        }
      });
  }

  private initNodesBulkUpdateSubscription() {
    this.contextMenuActionsService.nodesBulkUpdatedEmitter$
      .pipe(this.takeUntilComponentDestroy(), filter(Boolean))
      .subscribe((nodes: any[]) => {
        this.updateTagsWidth(nodes);

        nodes.forEach(node =>
          this.gridQuery.store.update(node._id, (entity: any) => {
            return {
              ...entity,
              ...this.getBulkUpdatedColumns(node)
            };
          })
        );
      });
  }

  public onRowSelected(): void {
    this.rowSelectedSubscription = this.gridQuery.selectActive().subscribe(nodes => {
      if (Array.isArray(nodes)) {
        const modifiedNodes = nodes.map((node: any) => {
          return {...node, id: node._id};
        });
        this.handleRowSelect(modifiedNodes);
      }
    });
  }

  public handleRowSelect(selectedItems: ICustomView[]): void {
    this.selector.handleKeySelectionChange(selectedItems);
    if (selectedItems.length > 0) {
      this.selectedNode = selectedItems[selectedItems.length - 1];
      this.contextMenuViewService['selectedNode'] = this.selectedNode;
      this.selectRow.emit(this.selectedNode);
      this.menuItems = [];

      if (this.disableContextMenu) {
        return;
      }

      if (this.gridQuery.getActiveId().length > 1) {
        // setTimeout is needed to allow async dependency fetch to complete before generating context menu items.
        // When a node is selected, it triggers the sidebar (internal-sidebar.component.ts) to fetch dependencies
        // asynchronously via getRefModelNodeRelatedData(). After the fetch completes, it updates the store values
        // CAN_PASTE_ORG_NODE and CAN_PASTE_REF_NODE based on whether a node can be pasted. The context menu
        // items check these values to enable/disable the paste button. Without this delay, the menu items would
        // be generated before the paste state is updated, causing the paste button to be incorrectly disabled.
        document.body.style.cursor = 'wait';
        setTimeout(() => {
          this.menuItems = this.contextMenuViewService.getContextMenuForMultipleNodes();
          document.body.style.cursor = 'default';
        }, 1500);
      } else {
        document.body.style.cursor = 'wait';
        setTimeout(() => {
          this.menuItems = this.contextMenuViewService.getContextMenuItemsForCustomView();
          document.body.style.cursor = 'default';
        }, 3500);
      }
    }
  }

  /** A dateOnly column holds a calendar day at UTC midnight; rendering it in the viewer's timezone would shift the day. */
  private formatDate(value: string, dateOnly: boolean): string {
    if (!Date.parse(value)) {
      return value;
    }

    return dateOnly
      ? this.date.transform(value, DAY_FORMAT, 'UTC')
      : this.date.transform(value, constants.dateFormat, this.userTimezone);
  }

  private getColumnValue(column: ICustomViewColumn, rowData: ICustomView): string {
    const {path, queryPath, hasSubcolumns, subcolumn} = column;
    const columnsPath = queryPath ?? path;
    const columnValue = columnsPath
      .split('.')
      .reduce((value, currentPath) => value?.[currentPath], rowData);

    if (column.isCustomAttribute) {
      const valueFieldName = constants.customNodeAttributeValueFieldNameMap[column.customFieldType];
      const value = columnValue?.find(item => item?.field === column.referenceId)?.[valueFieldName];

      if (column.options) {
        const transformed = Array.isArray(value)
          ? value.map(
              val => column.options.find((option: any) => option.value === val)?.displayValue
            )
          : column.options.find((option: any) => option.value === value)?.displayValue;

        return (Array.isArray(transformed) ? transformed.join(', ') : transformed) ?? '';
      }

      return value ?? '';
    }

    return (hasSubcolumns ? columnValue?.[subcolumn.value] : columnValue) ?? '';
  }

  private calculateTagsColumnWidth(tags: string[]): number {
    return tags?.length > 0 ? tags?.length * AVERAGE_TAG_WIDTH : DEFAULT_COLUMN_WIDTH;
  }

  private updateTagsWidth(tags: string[]): void {
    if (typeof tags === 'string') {
      tags = String(tags).split(' ');
    }

    const newWidth = this.calculateTagsColumnWidth(tags);
    this.updateTagsColumnWidth$.next(newWidth);
  }

  private getBulkUpdatedColumns(node: any) {
    const cnaColumns = this.columns$
      .getValue()
      .filter(column => column.isCustomAttribute)
      .reduce((transformed, column) => {
        const value = column.getColumnContent(node);

        return {...transformed, ...value};
      }, {});
    const updatedColumns = Object.keys(BULK_UPDATABLE_COLUMNS_PATH).reduce((transformed, field) => {
      const path = BULK_UPDATABLE_COLUMNS_PATH[field];
      const value = get(node, path, '');

      return {...transformed, [field]: value};
    }, {});

    return {...cnaColumns, ...updatedColumns};
  }

  private getUpdatedColumns(data: {value: any; property: string}) {
    const {value, property} = data;

    switch (property) {
      case TAGS_PATH:
        return {tags: value};
      case OPTIMIZE_STATUS_PATH:
        return {optimize_status: value};
      case OPTIMIZE_ASSIGNEE_PATH:
        return {optimize_assignee: value};
      case CNA_PATH:
        return this.columns$
          .getValue()
          .filter(column => column.isCustomAttribute)
          .reduce((transformed, column) => {
            const colValue = column.getColumnContent(value);

            return {...transformed, [column.field]: colValue};
          }, {});
      default:
        return set({}, property, value);
    }
  }

  private handleEditError(error, name): void {
    let msg;

    switch (true) {
      case error.status === 400 && error.error?.error?.includes?.('at least 4'):
        msg = '"Starts with" filter should be at least 4 characters long';
        break;
      default:
        msg = this.translate.instant(error.error?.error || 'RUBIC_CUBE.EDITED_FILTERS_FAILURE', {
          name
        });
    }

    this.snackBarService.error(msg);
  }

  onGridStateChanged(event: {sort: any; filters: {field: string; operator: string; value: any}[]}) {
    const {filters, sort} = event;
    const currentView = this._customMetadataView$.getValue();
    if (!currentView) {
      return;
    }
    const refModelId = this.rmeStore.refModelId;
    const newFilters: ICustomViewFilter[] = filters.map(filter => {
      const base: ICustomViewFilter = {
        value: filter.value,
        column: filter.field,
        operator: filter.operator
      };

      if (filter.field === 'tags') {
        base.tags = filter.value;
        delete base.value;
      } else if (BULK_UPDATABLE_COLUMNS_PATH.hasOwnProperty(filter.field)) {
        base.list = filter.value;
        delete base.value;
      }

      return base;
    });
    const newSort = sort[0] && {
      column: sort[0].field,
      direction: sort[0].dir
    };
    const {filters: viewFilters, sort: currentSort} = currentView;

    const needToUpdateFilters = !isEqual(
      newFilters.map((filter: any) => pick(filter, ['column', 'value', 'operator'])),
      viewFilters.map(filter => pick(filter, ['column', 'value', 'operator']))
    );

    const needToUpdateSort = !isEqual(newSort, currentSort);
    // Safeguard for the occasional grid event that reports no diff even though state changed:
    // force one reload so we never miss an update. A genuine "filters cleared" is already caught
    // by `needToUpdateFilters` (non-empty -> empty), so we must NOT also treat every filter-less
    // action as a clear — doing so fired a redundant `filterEntities` on plain sorts (HYDRO-30150 #9).
    const isClear = !needToUpdateFilters && !needToUpdateSort;

    if (currentView.dynamicalView) {
      // A dynamical view has no server-persisted filters/sort: re-pushing the view through
      // `_customMetadataView$` lets `initializeColumns` sync the grid store and trigger a SINGLE
      // reload. Dispatching `filterEntities`/`sortEntities` here as well would fire redundant
      // (switchMap-cancelled) reloads — the duplicate sort/filter requests from HYDRO-30150.
      this._customMetadataView$.next({
        ...currentView,
        filters: newFilters,
        sort: newSort ?? currentView.sort
      });
      return;
    }

    if (currentView.cleanUpView) {
      this.customViewDaoService
        .getQCVWithSettings(refModelId, currentView._id, newFilters, newSort)
        .subscribe(
          response => {
            this.customViewService.updateCustomView(response);
            (isClear || needToUpdateFilters) && this.actions.dispatch(filterEntities({filters}));
            needToUpdateSort && this.actions.dispatch(sortEntities({sort}));
          },
          error => {
            this.handleEditError(error, currentView.name);
          }
        );
      return;
    }

    if (needToUpdateFilters || isClear) {
      this.customViewDaoService.updateFilters(refModelId, currentView._id, newFilters).subscribe(
        response => {
          this.customViewService.updateCustomView(response);
          this.actions.dispatch(filterEntities({filters}));

          const msg = this.translate.instant('RUBIC_CUBE.EDITED_FILTERS_SUCCESSFULLY', {
            name: currentView.name
          });
          this.snackBarService.success(msg);
        },
        error => {
          this.handleEditError(error, currentView.name);
        }
      );
    }

    if ((newSort && needToUpdateSort) || isClear) {
      this.customViewDaoService.updateSort(refModelId, currentView._id, {sort: newSort}).subscribe(
        response => {
          this.customViewService.updateCustomView(response);
          this.actions.dispatch(sortEntities({sort}));

          const msg = this.translate.instant('RUBIC_CUBE.EDITED_SORT_SUCCESSFULLY', {
            name: currentView.name
          });
          this.snackBarService.success(msg);
        },
        error => {
          this.handleEditError(error, currentView.name);
        }
      );
    }
  }

  ngOnDestroy(): void {
    if (this.rowSelectedSubscription) {
      this.rowSelectedSubscription.unsubscribe();
    }
    document.body.style.cursor = 'default';
  }
}
